/**
 * @providesModule @api/GetLeaguePlayers
 */

import Preferences from 'Preferences';
import RESTStore from 'RESTStore';
import {
  generateMockLeaguePlayer,
  LeaguePlayer,
  mockFailResponse,
  MOCK_RESPONSE_DELAY,
  USE_MOCK,
} from './LeagueInfo';
import {NativeModules} from 'react-native';
import NetClient from 'NetClient';
import {ErrorObjectType} from 'NetClientTypes';
import {LeaguePrizeClaimed} from '@api/Leagues';

export const LEAGUE_PLAYERS_FETCH_LIMIT = 200;
export const LEAGUE_PLAYERS_FETCH_OFFSET = 0;

export type GetLeaguePlayersVariables = {
  /**
   * ID desired league instance
   */
  league_instance_id: string;
  /**
   * The limit of how many players to fetch (default 50)
   */
  limit?: number;
  /**
   * If null it provides the limit number of users from position 1 - limit
   */
  offset?: number;
  /**
   * Represents a previously saved place of the current player. We only pass this param when we want specific leaderboard data to populate the ProgressiveLeaguesStatusScreen. See those components and ProgressiveLeaguesStatusScreen.utils.ts for more details.
   */
  previousPlace?: number;
  /**
   * Determines whether the response should include the prize status of the current player.
   * When set to true, the response will include the player’s prize status (available, not available, or claimed).
   * This should only be called when looking at a concluded league's leaderboard
   */
  prizeStatus?: boolean;
  /**
   * When set to true and called using a league instance ID, this allows the player to “spectate” (get information from)
   * a league they are not participating in, including the leaderboard information (players and score).
   * When set to false (or not defined at all), the player must be querying information regarding the league they are participating in.
   */
  spectator?: boolean;
};

export type GetLeaguePlayersResult = {
  /**
   * The total number of players currently in the league
   */
  number_of_players: number;
  /**
   * The list of players for the requested page
   */
  players: LeaguePlayer[];
  /**
   * The current player
   */
  current_player: LeaguePlayer | null;
  /** The data regarding the current player's milestone progress, should it exist. Will only exist if milestones were configured for the league and the data appears in GetLeagues. */
  milestone_progress?: MilestoneProgress;
};

export type MilestoneProgress = {
  /** The id of the milestone set that the player is participating in. */
  milestone_set_id: string;
  /** The progress of the individual milestones for the player. */
  milestones: LeaguePlayerMilestone[];
};

export type LeaguePlayerMilestone = {
  /** The id of the milestone that the player is participating in. */
  milestone_id: string;
  /** The instance id of the milestone that the player is participating in. Should be milestone_id + occurrence_number. */
  milestone_instance_id: string;
  /** The prize status of the milestone for the player. AVAILABLE means the prize has not been claimed (could be eligible or ineligible for claim, will depend on how many medals the player has earned) and CLAIMED means the prize has been claimed already. */
  milestone_prize_status: 'AVAILABLE' | 'CLAIMED';
};

/** The request body for the claim league milestone rewards patch call. */
export type ClaimLeagueMilestoneRewardsRequestBody = {
  milestone_instance_id: string;
  prize_type: ClaimLeagueMilestoneRewardsPrizeType;
};

export type ClaimLeagueMilestoneRewardsPrizeType = 'Milestone'; // add other types here if they ever get added

export const getLeaguePlayersQueryKeyStrings = {
  getLeaguePlayers: 'getLeaguePlayers',
  getLeaguePlayersResults: 'getLeaguePlayersResults',
};

export const queryKeys = {
  getLeaguePlayers: (leagueInstanceId: string) => [
    getLeaguePlayersQueryKeyStrings.getLeaguePlayers,
    leagueInstanceId,
  ],
  /**
   * @param leagueInstanceId
   * @returns ['getLeaguePlayersResults', {leagueInstanceId}]
   */
  getLeaguePlayersResults: (leagueInstanceId: string) =>
    [getLeaguePlayersQueryKeyStrings.getLeaguePlayersResults, leagueInstanceId] as const,
} as const;

/**
 * The get call to the Back-End to get the list of players in the given league.
 * @param variables {GetLeaguePlayersVariables} the variable with the league instance id and the pagination info
 * @see https://skillzsdk.docs.apiary.io/#reference/leagues/league-participants/get-league-participants
 * @returns A Promise of the paginated list of the players in the given league
 */
export const getLeaguePlayers = (variables: GetLeaguePlayersVariables) => {
  return new Promise(
    (resolve: (playersResult: GetLeaguePlayersResult) => void, reject: (error: Error) => void) => {
      if (USE_MOCK) {
        const limit = variables.limit ?? players;
        setTimeout(
          () =>
            getMockFailPlayerResponse()
              ? reject(mockFailResponse)
              : resolve(
                  mockGetLeagueMultiPlayersResultPaginated(getMockStartingIndex(variables), limit)
                ),
          MOCK_RESPONSE_DELAY
        );
      } else {
        const {league_instance_id, ...queryParams} = variables;
        if (!league_instance_id) {
          resolve({
            number_of_players: 0,
            players: [],
            current_player: null,
          });
          return;
        }

        RESTStore.getServerResponse(
          'engagement/leagues/' + league_instance_id + '/players',
          queryParams,
          NativeModules.SKZAPIManager.OnError.failSilently,
          (response: GetLeaguePlayersResult, error: any | null) => {
            if (error) {
              if (error?.code === 404) {
                /** If the league is not found, return an empty list of players
                 * Handled this way for progressive leagues, where a league tier does not have players yet
                 */
                resolve({
                  number_of_players: 0,
                  players: [],
                  current_player: null,
                });
              }
              reject(error);
            } else {
              resolve(response);
            }
          }
        );
      }
    }
  );
};

/**
 * The patch call to the Back-End to claim the League Conclude rewards for the current player.
 * @param concludedLeagueId the id of the concluded league
 * @returns A Promise of the claim prize data
 */
export const claimLeagueRewards = async (concludedLeagueId: string) => {
  return new Promise(
    (
      resolve: (claimPrize: LeaguePrizeClaimed) => void,
      reject: (error: ErrorObjectType | null) => void
    ) => {
      NetClient.patchURL(
        'engagement/leagues/' + concludedLeagueId + '/players',
        {},
        NativeModules.SKZAPIManager.OnError.failSilently,
        (error: ErrorObjectType | null, payload: LeaguePrizeClaimed | null) => {
          if (payload && !error) {
            resolve(payload);
          } else {
            reject(error);
          }
        }
      );
    }
  );
};

/**
 * The patch call to the Back-End to claim the League Milestone rewards for the current player.
 * @param leagueInstanceId the id of the league instance (so we know what path to patch)
 * @param milestoneInstanceId the id of the milestone instance (so we know which milestone to claim)
 * @returns A Promise that resolves with true if successful, rejects with error if not.
 */
export const claimLeagueMilestoneRewards = async (
  leagueInstanceId: string,
  milestoneInstanceId: string
) => {
  const requestBody: ClaimLeagueMilestoneRewardsRequestBody = {
    milestone_instance_id: milestoneInstanceId,
    prize_type: 'Milestone',
  };

  return new Promise(
    (resolve: (didSucceed: boolean) => void, reject: (error: ErrorObjectType | null) => void) => {
      NetClient.patchURL(
        'engagement/leagues/' + leagueInstanceId + '/players',
        requestBody,
        NativeModules.SKZAPIManager.OnError.failSilently,
        (error: ErrorObjectType | null, payload: {} | null) => {
          if (payload && !error) {
            resolve(true);
          } else {
            reject(error);
          }
        }
      );
    }
  );
};

/** MOCK IMPLEMENTATIONS BELOW */

const players = 203;
const DEFAULT_CURRENT_PLACE = 'Default_Current_Mock_User_Place';
const MOCK_FAIL_PLAYER_RESPONSE = 'MOCK_FAIL_LEAGUE_PLAYER_RESPONSE';
const DEFAULT_FAIL = false;
const MOCK_EMPTY_PLAYER_RESPONSE = 'MOCK_EMPTY_PLAYER_RESPONSE';
const DEFAULT_FAIL_EMPTY = false;

/**
 * Allow SDK dev to set this while server is ready to provide real data
 * @param place the place we want the current user at for the following calls, if not provided set to default
 */
export const setMockCurrentUserPlace = (place?: number) => {
  Preferences.set({[DEFAULT_CURRENT_PLACE]: place});
};

export const getMockCurrentUserPlace = () => {
  return Preferences.get(DEFAULT_CURRENT_PLACE) || 15;
};

export const setMockFailPlayerResponse = (isFail?: boolean) => {
  Preferences.set({[MOCK_FAIL_PLAYER_RESPONSE]: isFail});
};

export const getMockFailPlayerResponse = () => {
  return Preferences.get(MOCK_FAIL_PLAYER_RESPONSE) || DEFAULT_FAIL;
};

export const setMockEmptyPlayerResponse = (isFail?: boolean) => {
  Preferences.set({[MOCK_EMPTY_PLAYER_RESPONSE]: isFail});
};

export const getMockEmptyPlayerResponse = () => {
  return Preferences.get(MOCK_EMPTY_PLAYER_RESPONSE) || DEFAULT_FAIL_EMPTY;
};

const avatars = [
  'https://cdn.staging.skillz.com/default-profile-pics/Girl2_010.png',
  'https://cdn.staging.skillz.com/default-profile-pics/Guy1_006.png',
  'https://cdn.staging.skillz.com/default-profile-pics/Girl2_003.png',
  'https://cdn.staging.skillz.com/default-profile-pics/Girl3_006.png',
  'https://cdn.staging.skillz.com/default-profile-pics/Guy1_005.png',
  'https://cdn.staging.skillz.com/default-profile-pics/Girl1_008.png',
  'https://cdn.staging.skillz.com/default-profile-pics/Guy1_007.png',
  'https://cdn.staging.skillz.com/default-profile-pics/Girl2_009.png',
  'https://cdn.staging.skillz.com/default-profile-pics/Girl1_007.png',
  'https://cdn.staging.skillz.com/default-profile-pics/Guy1_003.png',
];
export const createNNumberItems = (
  startingAt: number,
  numberToBeGenerated: number,
  totalPlayerCount?: number
) => {
  const list: LeaguePlayer[] = [];
  if (numberToBeGenerated <= 0) {
    return list;
  }
  const finalPlayerCount = totalPlayerCount ?? players;
  const realStartingIndex = startingAt + 1; // the real api takes an offset which shows the next ranked item to the limit (i.e adding one here recreates that)
  for (let step = realStartingIndex; step < realStartingIndex + numberToBeGenerated; step++) {
    if (step <= finalPlayerCount) {
      list.push(
        generateMockLeaguePlayer({
          user_id: step.toString(),
          place: step,
          thumbnail_url: avatars[step % avatars.length],
          name: `name ${step}`,
          // eslint-disable-next-line no-magic-numbers
          medals: Math.floor(10000 / step),
        })
      );
    }
  }
  return list;
};

const getMockStartingIndex = (variables: GetLeaguePlayersVariables) => {
  let startingIndex = 0;
  if (variables.offset) {
    startingIndex = variables.offset ?? startingIndex;
  }
  return startingIndex;
};

const mockGetLeagueMultiPlayersResultPaginated = (
  startingAt: number,
  numberToBeGenerated: number
) => {
  const result: GetLeaguePlayersResult = {
    number_of_players: getMockEmptyPlayerResponse() ? 0 : players,
    players: getMockEmptyPlayerResponse()
      ? []
      : createNNumberItems(startingAt, numberToBeGenerated, players),
    current_player: generateMockLeaguePlayer({
      user_id: getMockCurrentUserPlace().toString(),
      place: getMockCurrentUserPlace(),
      thumbnail_url: avatars[getMockCurrentUserPlace() % avatars.length],
      name: `name ${getMockCurrentUserPlace()}`,
    }),
  };
  return result;
};

export const mockGetLeagueEmptyResult: GetLeaguePlayersResult = {
  number_of_players: 0,
  players: [],
  current_player: null,
};

export const mockGetLeaguePlayersResult: GetLeaguePlayersResult = {
  number_of_players: 1,
  players: [generateMockLeaguePlayer()],
  current_player: generateMockLeaguePlayer(),
};

export const mockGetLeaguePlayersWithPlayerOverride = (
  numberOfPlayers: number,
  playerPosition: number,
  override?: Partial<LeaguePlayer>
) => {
  let playersList = createNNumberItems(0, numberOfPlayers, numberOfPlayers);
  playersList[playerPosition] = {...playersList[playerPosition], ...override} as LeaguePlayer;
  const result: GetLeaguePlayersResult = {
    number_of_players: numberOfPlayers,
    players: playersList,
    current_player: {...playersList[playerPosition], ...override} as LeaguePlayer,
  };
  return result;
};
